import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import {
  decodeFT60Frequency,
  encodeFT60Frequency,
} from './ft60-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const CHANNEL_OFFSET = 0x0168
const CHANNEL_SIZE = 16
const NAME_OFFSET = 0x0f28
const NAME_SIZE = 8
const SKIP_OFFSET = 0x1768
const BLOCK_SIZE = 32
const MEMORY_SIZE = 6057
const CHECKSUM_OFFSET = MEMORY_SIZE - 1
const CHANNEL_COUNT = 200
const CHARSET =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ          *+,- /|      [ ] _'
const STEPS = [5, 10, 12.5, 15, 20, 25, 50, 100] as const

type VX170ProfileSpec = {
  id: string
  model: string
  identity: string
  chirpModel?: string
  summary: string
  bands: readonly (readonly [number, number])[]
}

export type VX170Profile = {
  protocol: 'vx170'
  id: string
  vendor: 'Yaesu'
  model: string
  chirpModel: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 200
  nameLength: 6
  powerLevels: readonly ['Hi', 'Med', 'Lo']
  memorySize: number
  identity: readonly number[]
  bands: readonly (readonly [number, number])[]
}

function ascii(value: string): readonly number[] {
  return Array.from(new TextEncoder().encode(value))
}

function createProfile(spec: VX170ProfileSpec): VX170Profile {
  return {
    protocol: 'vx170',
    id: spec.id,
    vendor: 'Yaesu',
    model: spec.model,
    chirpModel: spec.chirpModel ?? spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Yaesu clone',
      'Read + write',
      '200 channels',
      '6-character names',
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 6,
    powerLevels: ['Hi', 'Med', 'Lo'],
    memorySize: MEMORY_SIZE,
    identity: ascii(spec.identity),
    bands: spec.bands,
  }
}

export const VX170_PROFILES: readonly VX170Profile[] = [
  createProfile({
    id: 'yaesu-vx-170',
    model: 'VX-170',
    identity: 'AH022$',
    summary:
      'Yaesu VX-170 VHF handheld with 200 memories and clone-mode programming.',
    bands: [[137_000_000, 174_000_000]],
  }),
  createProfile({
    id: 'yaesu-ft-270',
    model: 'FT-270',
    identity: 'AH022$',
    chirpModel: 'VX-170',
    summary:
      'FT-270 uses the Yaesu VX-170 clone layout and AH022 identity.',
    bands: [[137_000_000, 174_000_000]],
  }),
  createProfile({
    id: 'yaesu-vx-177',
    model: 'VX-177',
    identity: 'AH022U',
    summary:
      'Yaesu VX-177 UHF handheld using the same clone protocol as the VX-170.',
    bands: [[420_000_000, 470_000_000]],
  }),
]

function emptyChannel(
  number: number,
  profile: VX170Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function decodeName(
  image: Uint8Array,
  profile: VX170Profile,
  index: number,
): string {
  const offset = NAME_OFFSET + index * NAME_SIZE
  if ((image[offset + 7] & 0x80) === 0) return ''
  let name = ''
  for (let character = 0; character < profile.nameLength; character += 1) {
    const code = image[offset + character]
    if (code === 0xff) break
    name += CHARSET[code & 0x7f] ?? ' '
  }
  return name.trimEnd()
}

function encodeName(
  image: Uint8Array,
  profile: VX170Profile,
  index: number,
  name: string,
): void {
  const offset = NAME_OFFSET + index * NAME_SIZE
  const normalized = name.trim().toUpperCase().slice(0, profile.nameLength)
  for (let character = 0; character < profile.nameLength; character += 1) {
    const index = CHARSET.indexOf(normalized[character] ?? ' ')
    image[offset + character] = index < 0 ? 36 : index
  }
  image[offset + 6] = normalized ? 0x80 : 0
  image[offset + 7] = normalized ? 0x80 : 0
}

function offsetForChannel(raw: Uint8Array): number {
  return raw[12] * 50_000
}

function txFrequencyForChannel(
  raw: Uint8Array,
  rxFrequency: number,
  duplex: number,
): number | null {
  if (duplex === 2) return rxFrequency - offsetForChannel(raw)
  if (duplex === 3) return rxFrequency + offsetForChannel(raw)
  if (duplex === 4) return decodeFT60Frequency(raw.slice(5, 8))
  return rxFrequency
}

function decodeTone(
  raw: Uint8Array,
): Pick<WebChannel, 'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'> {
  const mode = raw[4] & 0x07
  if (mode === 4) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: DTCS_CODES[raw[9] & 0x7f] ?? DTCS_CODES[0],
      dtcsPolarity: 'N',
    }
  }
  if (mode >= 1 && mode <= 3) {
    return {
      toneMode: 'ctcss',
      ctcss: CTCSS_TONES[raw[8] & 0x3f] ?? CTCSS_TONES[0],
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
}

export function parseVX170Channel(
  image: Uint8Array,
  profile: VX170Profile,
  number: number,
): WebChannel {
  if (number < profile.channelMinimum || number > profile.channelMaximum) {
    throw new Error(`Channel ${number} is outside the ${profile.model} range.`)
  }
  const index = number - profile.channelMinimum
  const offset = CHANNEL_OFFSET + index * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || (raw[0] & 0x80) === 0) {
    return emptyChannel(number, profile)
  }
  const rxFrequency = decodeFT60Frequency(raw.slice(1, 4))
  if (!rxFrequency) return emptyChannel(number, profile)
  const duplex = raw[0] & 0x07
  const skipIndex =
    (image[SKIP_OFFSET + ((number - 1) >> 2)] >>
      (((number - 1) & 3) * 2)) &
    0x03
  return {
    number,
    empty: false,
    name: decodeName(image, profile, index),
    rxFrequency,
    txFrequency: txFrequencyForChannel(raw, rxFrequency, duplex),
    ...decodeTone(raw),
    mode: (raw[0] & 0x20) !== 0 ? 'NFM' : 'FM',
    power: profile.powerLevels[(raw[8] >> 6) & 0x03] ?? profile.powerLevels[0],
    skip: skipIndex !== 0,
  }
}

export function parseVX170Channels(
  image: Uint8Array,
  profile: VX170Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseVX170Channel(image, profile, index + profile.channelMinimum),
  )
}

function requiredStep(frequency: number): number {
  for (const step of STEPS) {
    if (frequency % Math.round(step * 1000) === 0) return step
  }
  return STEPS[0]
}

function encodeTone(raw: Uint8Array, channel: WebChannel): void {
  const previousMode = raw[4] & 0x07
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('Unsupported CTCSS tone for this Yaesu.')
    raw[4] =
      (raw[4] & 0xf8) |
      ([1, 2, 3].includes(previousMode) ? previousMode : 1)
    raw[8] = (raw[8] & 0xc0) | index
    raw[9] &= 0x80
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number])
    if (index < 0) throw new Error('Unsupported DTCS code for this Yaesu.')
    raw[4] = (raw[4] & 0xf8) | 4
    raw[9] = (raw[9] & 0x80) | index
    raw[8] &= 0xc0
  } else {
    raw[4] &= 0xf8
    raw[8] &= 0xc0
    raw[9] &= 0x80
  }
}

function writeOffset(raw: Uint8Array, units: number): void {
  if (units < 0 || units > 0xff) {
    throw new Error('This repeater shift cannot be represented by the radio.')
  }
  raw[12] = units
}

export function updateVX170Checksum(image: Uint8Array): void {
  let checksum = 0
  for (let index = 0; index < CHECKSUM_OFFSET; index += 1) {
    checksum = (checksum + image[index]) & 0xff
  }
  image[CHECKSUM_OFFSET] = checksum
}

export function applyVX170Channel(
  image: Uint8Array,
  profile: VX170Profile,
  channel: WebChannel,
): void {
  const index = channel.number - profile.channelMinimum
  const offset = CHANNEL_OFFSET + index * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw[0] &= 0x7f
    updateVX170Checksum(image)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if ((raw[0] & 0x80) === 0) raw.fill(0)
  raw[0] =
    (raw[0] & 0x1b) |
    0x80 |
    (channel.mode === 'NFM' ? 0x20 : 0)
  raw.set(encodeFT60Frequency(channel.rxFrequency), 1)

  const txFrequency = channel.txFrequency
  if (txFrequency === null || txFrequency === channel.rxFrequency) {
    raw[0] &= 0xf8
    raw.fill(0, 5, 8)
    writeOffset(raw, 0)
  } else {
    const delta = txFrequency - channel.rxFrequency
    const units = Math.abs(delta) / 50_000
    const preserveSplit = (raw[0] & 0x07) === 4
    if (!preserveSplit && Number.isInteger(units) && units >= 1 && units <= 0xff) {
      raw[0] = (raw[0] & 0xf8) | (delta < 0 ? 2 : 3)
      raw.fill(0, 5, 8)
      writeOffset(raw, units)
    } else {
      raw[0] = (raw[0] & 0xf8) | 4
      raw.set(encodeFT60Frequency(txFrequency), 5)
      writeOffset(raw, 0)
    }
  }

  encodeTone(raw, channel)
  const powerIndex = Math.max(0, profile.powerLevels.indexOf(
    channel.power as VX170Profile['powerLevels'][number],
  ))
  raw[8] = (raw[8] & 0x3f) | ((powerIndex & 0x03) << 6)
  raw[4] =
    (raw[4] & 0x8f) |
    ((STEPS.indexOf(
      requiredStep(channel.rxFrequency) as (typeof STEPS)[number],
    ) &
      0x07) <<
      4)
  const skipOffset = SKIP_OFFSET + ((channel.number - 1) >> 2)
  const skipShift = ((channel.number - 1) & 3) * 2
  const oldSkip = (image[skipOffset] >> skipShift) & 0x03
  const nextSkip = channel.skip ? oldSkip || 1 : 0
  image[skipOffset] =
    (image[skipOffset] & ~(0x03 << skipShift)) | (nextSkip << skipShift)
  encodeName(image, profile, index, channel.name)
  updateVX170Checksum(image)
}

async function writeEcho(
  transport: RadioTransport,
  data: Uint8Array,
  timeoutMs = 2500,
): Promise<void> {
  await transport.write(data)
  const echo = await transport.readExact(data.byteLength, timeoutMs)
  if (compareImages(data, echo) !== null) {
    throw new Error('The Yaesu cable returned a corrupted command echo.')
  }
}

async function readAck(transport: RadioTransport, label: string): Promise<void> {
  const response = await transport.readExact(1, 2500)
  if (response[0] !== ACK) {
    throw new Error(`The ${label} did not acknowledge the programming block.`)
  }
}

export async function readVX170RadioWithTransport(
  profile: VX170Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  const image = new Uint8Array(profile.memorySize)
  const identity = await transport.readExact(8, 6000)
  image.set(identity)
  const reportedModel = new TextDecoder('ascii')
    .decode(identity.slice(0, 6))
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
  if (
    !profile.identity.every((value, index) => identity[index] === value)
  ) {
    throw new Error(
      `The connected radio does not identify as the selected ${profile.model}.`,
    )
  }
  await writeEcho(transport, Uint8Array.of(ACK))

  let imageOffset = identity.byteLength
  const total = 6048 / BLOCK_SIZE + 1
  for (let block = 0; block < 6048 / BLOCK_SIZE; block += 1) {
    const payload = await transport.readExact(BLOCK_SIZE, 3500)
    image.set(payload, imageOffset)
    imageOffset += payload.byteLength
    await writeEcho(transport, Uint8Array.of(ACK))
    onProgress?.({
      current: block + 1,
      total,
      label: `Reading ${profile.model} memory`,
    })
  }
  image.set(await transport.readExact(1, 3500), imageOffset)
  await writeEcho(transport, Uint8Array.of(ACK))
  onProgress?.({ current: total, total, label: `Reading ${profile.model} memory` })
  return { image, reportedModel: reportedModel || profile.model }
}

export async function writeVX170RadioWithTransport(
  profile: VX170Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error(`The selected image is smaller than the ${profile.model} memory map.`)
  }
  updateVX170Checksum(image)
  let offset = 0
  const total = 8 / BLOCK_SIZE + 6048 / BLOCK_SIZE + 1
  let completed = 0
  for (const blockLength of [8, 6048, 1]) {
    for (let block = 0; block < blockLength; block += BLOCK_SIZE) {
      const payload = image.slice(
        offset,
        Math.min(offset + BLOCK_SIZE, offset + blockLength),
      )
      await writeEcho(transport, payload)
      await readAck(transport, profile.model)
      offset += payload.byteLength
      completed += 1
      onProgress?.({
        current: completed,
        total,
        label: `Writing ${profile.model} memory`,
      })
    }
  }
}
